import { Component } from '@theme/component';

/**
 * Announcement banner custom element.
 *
 * Supports two display modes:
 *   1. `fade`   - the original behaviour: cross-fade between blocks on a timer.
 *   2. `scroll` - a continuous horizontal marquee. Content is duplicated for a
 *                 seamless loop. Speed (px/s) and direction (normal | reverse)
 *                 are driven by attributes from the section liquid.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [slideshowContainer]
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLElement} [marquee]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /** @type {number|undefined} */
  #interval = undefined;

  /** @type {ResizeObserver|undefined} */
  #resizeObserver = undefined;

  /** @type {HTMLElement|null} */
  #marquee = null;

  /** @type {HTMLElement[]} Original (pre-clone) marquee children. */
  #originals = [];

  /** @type {boolean} */
  #layoutQueued = false;

  /** @type {string} Signature of the last build, to skip needless re-clones. */
  #layoutKey = '';

  connectedCallback() {
    super.connectedCallback();

    if (this.mode === 'scroll') {
      this.#setupScroll();
      return;
    }

    // Fade mode (original behaviour).
    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.suspend();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /**
   * Current display mode, derived from the `mode` attribute.
   * @returns {'fade'|'scroll'}
   */
  get mode() {
    return this.getAttribute('mode') === 'scroll' ? 'scroll' : 'fade';
  }

  // ---------- Scroll mode ----------

  #setupScroll() {
    const marquee =
      /** @type {HTMLElement|null} */
      (this.querySelector('.announcement-bar__marquee'));
    if (!marquee) return;

    this.#marquee = marquee;
    this.#originals = /** @type {HTMLElement[]} */ (Array.from(marquee.children));
    if (this.#originals.length === 0) return;

    this.#layoutMarquee();

    // Re-layout when content size changes — Trustpilot iframes finishing load,
    // web fonts, or viewport resize. The host has `overflow: hidden`, so its box
    // tracks the viewport width (used for the copy count) without ever growing
    // to the scrolling content (which would loop the observer).
    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => this.#scheduleLayout());
      this.#resizeObserver.observe(this);
    }

    // Pause on hover so users can read announcements.
    this.addEventListener('mouseenter', this.#pauseMarquee);
    this.addEventListener('mouseleave', this.#resumeMarquee);

    // Pause when tab is hidden to save CPU.
    document.addEventListener('visibilitychange', this.#handleScrollVisibility);
  }

  /**
   * Re-initialize any Trustpilot widgets we cloned into the marquee.
   * The global Trustpilot loader scans the DOM once at boot; nodes added
   * afterwards need a manual loadFromElement call to render.
   * @param {HTMLElement} container
   */
  #initTrustpilotClones(container) {
    const widgets = container.querySelectorAll(
      '[aria-hidden="true"] .trustpilot-widget'
    );
    if (widgets.length === 0) return;

    let attempts = 0;
    const tryInit = () => {
      const tp = /** @type {any} */ (window).Trustpilot;
      if (tp && typeof tp.loadFromElement === 'function') {
        widgets.forEach((el) => tp.loadFromElement(el, true));
        return;
      }
      if (attempts++ < 50) setTimeout(tryInit, 200); // up to ~10s
    };
    tryInit();
  }

  /** Debounce re-layout to once per animation frame. */
  #scheduleLayout = () => {
    if (this.#layoutQueued) return;
    this.#layoutQueued = true;
    requestAnimationFrame(() => {
      this.#layoutQueued = false;
      this.#layoutMarquee();
    });
  };

  /**
   * Build (or rebuild) the marquee for a seamless loop:
   *   1. Measure one original set's width, including the gap that separates it
   *      from the next copy.
   *   2. Duplicate the set enough times to always cover the viewport — so the
   *      bar is never empty mid-scroll (the cause of the "gap, then refresh").
   *   3. Set the animation distance to exactly one set, so copies tile.
   * Re-clones only when the structure actually changes, so resize ticks and
   * Trustpilot iframe loads don't re-initialise the widgets on every frame.
   */
  #layoutMarquee() {
    const marquee = this.#marquee;
    const originals = this.#originals;
    if (!marquee || originals.length === 0) return;

    const styles = getComputedStyle(marquee);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;

    let setWidth = 0;
    for (const el of originals) setWidth += el.getBoundingClientRect().width;
    setWidth += gap * originals.length; // inter-item gaps + one trailing gap
    if (setWidth <= 0) return;

    const viewport =
      this.clientWidth || marquee.parentElement?.clientWidth || setWidth;
    const copies = Math.max(2, Math.ceil((viewport + setWidth) / setWidth));

    // Only rebuild clones when the set width or copy count actually changed.
    const key = `${Math.round(setWidth)}:${copies}`;
    if (key !== this.#layoutKey) {
      this.#layoutKey = key;

      while (marquee.children.length > originals.length) {
        marquee.removeChild(/** @type {Node} */ (marquee.lastElementChild));
      }
      for (let copy = 1; copy < copies; copy++) {
        for (const node of originals) {
          const clone = /** @type {HTMLElement} */ (node.cloneNode(true));
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('ref');
          clone.querySelectorAll('[ref]').forEach((el) => el.removeAttribute('ref'));
          marquee.appendChild(clone);
        }
      }

      // Trustpilot's loader only scans the DOM at boot; render the clones we
      // just added.
      this.#initTrustpilotClones(marquee);
    }

    const speed = Math.max(parseFloat(this.getAttribute('scroll-speed') || '60'), 1);
    marquee.style.setProperty('--marquee-distance', `${setWidth}px`);
    marquee.style.setProperty('--marquee-duration', `${setWidth / speed}s`);
  }

  #pauseMarquee = () => {
    this.#marquee?.style.setProperty('--marquee-play-state', 'paused');
  };

  #resumeMarquee = () => {
    this.#marquee?.style.setProperty('--marquee-play-state', 'running');
  };

  #handleScrollVisibility = () => {
    if (!this.#marquee) return;
    this.#marquee.style.setProperty(
      '--marquee-play-state',
      document.hidden ? 'paused' : 'running'
    );
  };

  // ---------- Fade mode (original) ----------

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in ms between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused) return;
    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);
    if (Number.isNaN(value)) return undefined;
    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /** Pause the fade slideshow when the tab is hidden. */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
